/**
 * The React binding for the headless creator: one hook that any renderer — DOM
 * inputs, React Native controls, a third-party host's own design system — drives.
 * It owns no markup, so the UI layer stays thin on every surface (ADR 0017).
 */

import { restoreCreatorState } from './session';
import { useCallback, useMemo, useReducer, useRef } from 'react';
import {
  type CalendarLink,
  type EventDraft,
  type EventProblem,
  EVENT_FORMATS,
  addToCalendarLinks,
  icsTimestamp,
} from '@project/event-format';

import { type EventExportResult, exportDraft } from './export';
import {
  type EventCreatorAction,
  type EventCreatorDefaults,
  type EventCreatorState,
  eventCreatorReducer,
  initialState,
  withLoadedDraft,
  problemFor,
  visibleProblems,
} from './state';

export interface UseEventCreatorOptions {
  readonly defaults: EventCreatorDefaults;
  /** Start from an existing event (an AI-proposed draft, an edit, a host prefill). */
  readonly initialDraft?: EventDraft;
  /** Versioned hosts may restore unfinished state without sanitizing away incomplete fields. */
  readonly initialSession?: unknown;
  /** Injected for tests and for hosts that want their own identity scheme. */
  readonly createUid?: () => string;
  readonly now?: () => Date;
}

export interface EventCreator {
  readonly state: EventCreatorState;
  readonly draft: EventDraft;
  readonly dispatch: (action: EventCreatorAction) => void;
  /** Problems to render right now — empty until the user tries to export. */
  readonly problems: readonly EventProblem[];
  readonly problemFor: (field: string) => EventProblem | undefined;
  readonly formats: typeof EVENT_FORMATS;
  readonly links: readonly CalendarLink[];
  /** Live text of the selected format, or `null` while the draft is invalid. */
  readonly preview: string | null;
  /** Validates, marks the form submitted, and returns the payload or the problems. */
  readonly requestExport: () => EventExportResult;
}

function defaultUid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}@event-format`;
}

export function useEventCreator(options: UseEventCreatorOptions): EventCreator {
  const { defaults, initialDraft, createUid = defaultUid, now = () => new Date() } = options;

  const [state, dispatch] = useReducer(
    eventCreatorReducer,
    { defaults, initialDraft, session: options.initialSession },
    ({ defaults: seed, initialDraft: draft, session }) => {
      const restored = restoreCreatorState(session);
      if (restored) return restored;
      const base = initialState(seed);
      // A prefill is the `load` case in disguise, so it takes the same path: sanitized, with
      // the session zone following the draft.
      return draft === undefined ? base : withLoadedDraft(base, draft);
    },
  );

  // Identity and stamp are fixed for the session so a preview doesn't churn on
  // every keystroke and a re-export updates the same event rather than cloning it.
  const context = useRef<{ uid: string; dtstamp: string } | null>(null);
  if (context.current === null) {
    context.current = { uid: state.draft.uid ?? createUid(), dtstamp: icsTimestamp(now()) };
  }
  const formatContext = context.current;

  const problems = useMemo(() => visibleProblems(state), [state]);
  const preview = useMemo(() => {
    const result = exportDraft(state.draft, state.formatId, formatContext);
    return result.ok ? result.export.content : null;
  }, [state.draft, state.formatId, formatContext]);
  const links = useMemo(() => addToCalendarLinks(state.draft), [state.draft]);

  const requestExport = useCallback((): EventExportResult => {
    dispatch({ type: 'submit' });
    return exportDraft(state.draft, state.formatId, formatContext);
  }, [state.draft, state.formatId, formatContext]);

  return {
    state,
    draft: state.draft,
    dispatch,
    problems,
    problemFor: useCallback((field: string) => problemFor(problems, field), [problems]),
    formats: EVENT_FORMATS,
    links,
    preview,
    requestExport,
  };
}
